import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search, Phone, Mail } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '../context/CartContext';
import { settingsAPI } from '../lib/api';
import { useApiQuery } from '../hooks/useApiQuery';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  const { data: settingsRes } = useApiQuery(['settings'], () => settingsAPI.get());
  const settings = settingsRes?.data || {};

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/products', label: 'Products' },
    { path: '/my-orders', label: 'My Orders' },
    { path: '/contact', label: 'Contact' },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setIsOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50" data-testid="navbar">
      {/* ── Info Bar ─────────────────────────────────────────── */}
      <div className="bg-[#0f172a] text-slate-300 text-xs py-2 hidden md:block">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl flex justify-between items-center">
          <div className="flex items-center gap-6">
            {settings.phone && (
              <a
                href={`tel:${settings.phone}`}
                className="flex items-center gap-1.5 hover:text-[#8acb95] transition-colors"
              >
                <Phone className="w-3 h-3" />
                {settings.phone}
              </a>
            )}
            {settings.email && (
              <a
                href={`mailto:${settings.email}`}
                className="flex items-center gap-1.5 hover:text-[#8acb95] transition-colors"
              >
                <Mail className="w-3 h-3" />
                {settings.email}
              </a>
            )}
            {!settings.phone && !settings.email && (
              <span className="flex items-center gap-1.5">
                <Phone className="w-3 h-3 text-[#8acb95]" />
                Gulf Star Trading
              </span>
            )}
          </div>

        </div>
      </div>

      {/* ── Main Bar ─────────────────────────────────────────── */}
      <div className="glass-effect border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center gap-4 h-16">

            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2.5 flex-shrink-0"
              data-testid="navbar-logo"
            >
              <img
                src="/gulf-star-logo.jpeg"
                alt="Gulf Star"
                className="h-10 w-10 object-contain rounded-lg"
              />
              <span
                className="text-xl font-bold tracking-tight text-slate-800 hidden sm:block"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Gulf Star
              </span>
            </Link>

            {/* Search Bar — desktop */}
            <form onSubmit={handleSearch} className="flex-1 max-w-2xl hidden md:flex">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search for products, brands, categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-4 pr-12 py-2.5 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] transition-colors bg-white"
                />
                <button
                  type="submit"
                  className="absolute right-0 top-0 h-full px-4 bg-[#8acb95] hover:bg-[#7bb885] text-white rounded-r-xl transition-colors flex items-center"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center gap-2 ml-auto md:ml-0">
              <Link
                to="/cart"
                className="relative flex items-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white px-4 py-2 rounded-xl transition-all font-medium text-sm shadow-sm"
                data-testid="nav-cart"
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="hidden sm:block">Cart</span>
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center shadow">
                    {cartCount > 9 ? '9+' : cartCount}
                  </span>
                )}
              </Link>

              {/* Mobile hamburger */}
              <button
                className="md:hidden p-2 text-slate-600 hover:text-[#8acb95] rounded-lg hover:bg-slate-100 transition-colors"
                onClick={() => setIsOpen(!isOpen)}
                data-testid="mobile-menu-toggle"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* ── Nav Links Strip — desktop ─────────────────── */}
          <div className="hidden md:flex items-center gap-1 pb-3 border-t border-slate-100 pt-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                  isActive(link.path)
                    ? 'bg-[#8acb95] text-white shadow-sm'
                    : 'text-slate-600 hover:bg-green-50 hover:text-[#6ab57e]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* ── Mobile Drawer ─────────────────────────────── */}
        {isOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
            {/* Mobile Search */}
            <form onSubmit={handleSearch} className="p-4 border-b border-slate-100">
              <div className="relative flex">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 pl-4 pr-4 py-2.5 border border-slate-200 rounded-l-xl text-sm focus:outline-none focus:border-[#8acb95]"
                />
                <button
                  type="submit"
                  className="px-4 bg-[#8acb95] text-white rounded-r-xl hover:bg-[#7bb885] transition-colors"
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
            </form>

            {/* Mobile Links */}
            <nav className="py-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center px-6 py-3.5 text-sm font-medium transition-colors border-b border-slate-50 last:border-0 ${
                    isActive(link.path)
                      ? 'text-[#8acb95] bg-green-50'
                      : 'text-slate-700 hover:text-[#8acb95] hover:bg-green-50'
                  }`}
                >
                  {isActive(link.path) && (
                    <span className="w-1.5 h-1.5 bg-[#8acb95] rounded-full mr-3" />
                  )}
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Mobile contact info */}
            {settings.phone && (
              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
                <a
                  href={`tel:${settings.phone}`}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#8acb95]"
                >
                  <Phone className="w-4 h-4 text-[#8acb95]" />
                  {settings.phone}
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
